import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {purchaseRequestGetAll, purchaseRequestExcel} from '../../../redux/slices/purchase.slice'
import {projectLookup, allCompanyLookup, basicLookup} from '../../../redux/slices/lookup.slice'

import {
  useMemoizedStaticData
} from '../../../utils/performanceOptimizations'

const selectPurchaseData = (state) => ({
  request: state.purchase.request,
  loading: state.purchase.loading
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  PurchaseRequestStatus: state.lookup.PurchaseRequestStatus
})

function List() {
  const intl = useIntl()
  const {request, loading} = useSelector(selectPurchaseData)
  const {projectLookupList, companyLookup, PurchaseRequestStatus} = useSelector(selectLookupData)
  const dispatch = useDispatch()

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(purchaseRequestGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(allCompanyLookup())
    dispatch(basicLookup('PurchaseRequestStatus'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      purchaseRequestExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.REQUEST'}),
      })
    )
  }, [dispatch, params, intl])

  const columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'REQUEST.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'REQUEST.SEARCH.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'REQUEST.SEARCH.COMPANYNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'companyName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'REQUEST.SEARCH.DUEDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'dueDate',
      Cell: ({...props}) => <CustomCellDouble item={props} date='short' double='delayDay' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'REQUEST.SEARCH.PURCHASEREQUESTSTATUSNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'purchaseRequestStatusName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'REQUEST.SEARCH.PROPOSALCOUNT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'proposalCount',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'REQUEST.SEARCH.PROJECTNAME',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'REQUEST.SEARCH.COMPANYNAME',
          data: companyLookup,
          name: 'companyId',
        },
        {
          title: 'REQUEST.SEARCH.PURCHASEREQUESTSTATUSNAME',
          data: PurchaseRequestStatus,
          name: 'purchaseRequestStatusId',
        },
      ],
    },
  ], [handleExcelExport, projectLookupList, companyLookup, PurchaseRequestStatus])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.REQUEST'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'REQUEST'}
        />
        <KTCardBody>
          {!request && <ListLoading />}
          {request && (
            <Table
              items={request}
              cols={columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
